import {NoteIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useMarkReadyForReviewMutation} from '@github-ui/mergebox/use-mark-ready-for-review-mutation'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {FailureDialog} from '../../sidebar-actions/FailureDialog'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {useOverviewRouteData} from '../../data/use-overview-route-data'
import {useInvalidatePrState} from '../../data/use-invalidate-pr-state'
import {PullRequestState} from '../../../../types'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

// Renders when the pull request is still in draft.
export function DraftPR() {
  const {pullRequest} = useLayoutRouteData()
  const {user} = useOverviewRouteData()
  const invalidatePrState = useInvalidatePrState()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const markReadyForReviewMutation = useMarkReadyForReviewMutation({
    onSuccess: () => {
      announce('Pull request marked as ready for review')
      invalidatePrState()
    },
  })
  const {mutate: markReadyForReview, isPending} = markReadyForReviewMutation

  if (pullRequest.state !== PullRequestState.Draft || !user?.canMarkReadyForReview) {
    return null
  }

  const handleMarkReadyForReview = () => {
    if (isPending) return
    sendPullRequestAnalyticsEvent('overview.for_you_mark_ready_for_review', 'OVERVIEW_FOR_YOU_ITEM')
    markReadyForReview()
  }

  return (
    <>
      <ForYouItem
        leadingVisual={<ForYouLeadingIcon icon={NoteIcon} variant="warning" />}
        title="Mark ready for review"
        itemName="draft-pr"
        actionSlot={
          <Button
            size="small"
            onClick={handleMarkReadyForReview}
            loading={isPending}
            loadingAnnouncement="Marking ready for review"
          >
            Ready for review
          </Button>
        }
      />
      {markReadyForReviewMutation.isError && (
        <FailureDialog
          title="Failed to mark ready for review"
          message="An error occurred while marking this pull request as ready for review. Please try again."
          details={markReadyForReviewMutation.error.message}
          onRetry={() => markReadyForReviewMutation.mutate()}
          onDismiss={() => markReadyForReviewMutation.reset()}
        />
      )}
    </>
  )
}
